import { z } from "zod";
import { fieldDefSchema, type FieldDef } from "./fields";
import { slugSchema } from "./slug";

/** A persisted content type (schema definition). */
export const contentTypeSchema = z
  .object({
    id: z.string(),
    name: z.string().min(1).max(120),
    slug: slugSchema,
    /** Which field's value is used as the human-readable title / listing label. */
    titleFieldKey: z.string().min(1),
    fields: z.array(fieldDefSchema).min(1),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type ContentType = z.infer<typeof contentTypeSchema>;

interface ContentTypeShape {
  titleFieldKey: string;
  fields: FieldDef[];
}

/** Cross-field invariants shared by create & update. */
function refineContentType(ct: ContentTypeShape, ctx: z.RefinementCtx): void {
  const keys = ct.fields.map((f) => f.key);
  const duplicates = [...new Set(keys.filter((k, i) => keys.indexOf(k) !== i))];
  if (duplicates.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Duplicate field keys: ${duplicates.join(", ")}`,
      path: ["fields"],
    });
  }
  if (!keys.includes(ct.titleFieldKey)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `titleFieldKey "${ct.titleFieldKey}" is not a defined field`,
      path: ["titleFieldKey"],
    });
  }
  ct.fields.forEach((f, i) => {
    if (f.kind === "reference" && !f.config?.refTypeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Reference field "${f.key}" requires config.refTypeId`,
        path: ["fields", i, "config", "refTypeId"],
      });
    }
  });
}

export const createContentTypeInputSchema = z
  .object({
    name: z.string().min(1).max(120),
    slug: slugSchema,
    titleFieldKey: z.string().min(1),
    fields: z.array(fieldDefSchema).min(1),
  })
  .strict()
  .superRefine(refineContentType);
export type CreateContentTypeInput = z.infer<typeof createContentTypeInputSchema>;

export const updateContentTypeInputSchema = createContentTypeInputSchema;
export type UpdateContentTypeInput = z.infer<typeof updateContentTypeInputSchema>;
